<script setup>
import { ref, onMounted } from 'vue';
import { useMFA } from '@/composables/useMFA';
import { useWebAuthn } from '@/composables/useWebAuthn';

const emit = defineEmits(['enrolled', 'cancel']);

const mfa = useMFA();
const webauthn = useWebAuthn();

const step = ref(1); // 1: Info, 2: Registering, 3: Success
const passkeyName = ref('');
const isLoading = ref(false);
const errorMessage = ref('');
const hasPlatformAuth = ref(false);

// Check platform authenticator availability
onMounted(async () => {
  hasPlatformAuth.value = await webauthn.checkPlatformAuthenticator();
});

const startRegistration = async () => {
  if (!passkeyName.value.trim()) {
    errorMessage.value = 'Please enter a name for your passkey';
    return;
  }

  isLoading.value = true;
  errorMessage.value = '';
  step.value = 2;

  try {
    // Get registration options from server (includes challengeToken)
    const response = await mfa.getPasskeyRegisterOptions();
    const { options, challengeToken } = response || {};

    // Validate that we got proper options
    if (!options || (Array.isArray(options) && options.length === 0)) {
      throw new Error('Server returned invalid registration options. Please try again.');
    }
    if (!options.challenge) {
      throw new Error('Server did not provide a challenge for registration. Please try again.');
    }

    // Create credential with WebAuthn
    const credential = await webauthn.registerPasskey(options);

    // Send credential to server with challengeToken
    await mfa.registerPasskey(credential, challengeToken, passkeyName.value);

    step.value = 3;
  } catch (err) {
    step.value = 1;
    if (err.name === 'NotAllowedError') {
      errorMessage.value = 'Passkey registration was cancelled or timed out';
    } else if (err.name === 'InvalidStateError') {
      errorMessage.value = 'This passkey is already registered';
    } else {
      errorMessage.value = err.message || 'Failed to register passkey';
    }
  } finally {
    isLoading.value = false;
  }
};

const finishSetup = () => {
  emit('enrolled');
};
</script>

<template>
  <div class="passkey-enrollment">
    <!-- Step 1: Info & Name -->
    <div v-if="step === 1" class="enrollment-step">
      <div class="step-header">
        <div class="step-icon">
          <i class="fas fa-fingerprint"></i>
        </div>
        <h3>Add a Passkey</h3>
        <p>Passkeys are a secure, password-free way to sign in using your device's biometrics.</p>
      </div>

      <div class="passkey-features">
        <div class="feature">
          <div class="feature-icon">
            <i class="fas fa-bolt"></i>
          </div>
          <div class="feature-text">
            <strong>Fast</strong>
            <span>Sign in instantly with Face ID, Touch ID, or Windows Hello</span>
          </div>
        </div>
        <div class="feature">
          <div class="feature-icon">
            <i class="fas fa-shield-alt"></i>
          </div>
          <div class="feature-text">
            <strong>Secure</strong>
            <span>Protected by your device's secure enclave</span>
          </div>
        </div>
        <div class="feature">
          <div class="feature-icon">
            <i class="fas fa-ban"></i>
          </div>
          <div class="feature-text">
            <strong>Phishing-resistant</strong>
            <span>Cannot be stolen or used on fake websites</span>
          </div>
        </div>
      </div>

      <div class="name-input-container">
        <label for="passkey-name">Name your passkey</label>
        <input
          id="passkey-name"
          v-model="passkeyName"
          type="text"
          placeholder="e.g., MacBook Pro, iPhone"
          class="name-input"
          @keyup.enter="startRegistration"
        />
        <small>This helps you identify the device later</small>
      </div>

      <div v-if="errorMessage" class="error-message">
        <i class="fas fa-exclamation-circle"></i>
        {{ errorMessage }}
      </div>

      <div v-if="!webauthn.isSupported.value" class="warning-message">
        <i class="fas fa-exclamation-triangle"></i>
        Passkeys are not supported on this browser
      </div>

      <div class="step-actions">
        <button class="btn-secondary" @click="emit('cancel')">Cancel</button>
        <button
          class="btn-primary"
          @click="startRegistration"
          :disabled="!webauthn.isSupported.value || !passkeyName.trim()"
        >
          <i class="fas fa-plus"></i>
          Create Passkey
        </button>
      </div>
    </div>

    <!-- Step 2: Registering -->
    <div v-else-if="step === 2" class="enrollment-step">
      <div class="step-header">
        <div class="step-icon step-icon-registering">
          <div class="fingerprint-animation">
            <i class="fas fa-fingerprint"></i>
          </div>
        </div>
        <h3>Complete on Your Device</h3>
        <p>Follow the prompts on your device to create your passkey</p>
      </div>

      <div class="registering-animation">
        <div class="pulse-ring"></div>
        <div class="pulse-ring delay-1"></div>
        <div class="pulse-ring delay-2"></div>
      </div>

      <div class="device-prompt">
        <i class="fas fa-hand-pointer"></i>
        <span>Waiting for your device...</span>
      </div>
    </div>

    <!-- Step 3: Success -->
    <div v-else-if="step === 3" class="enrollment-step">
      <div class="step-header">
        <div class="step-icon step-icon-success">
          <i class="fas fa-check"></i>
        </div>
        <h3>Passkey Created!</h3>
        <p>Your passkey "{{ passkeyName }}" is ready to use</p>
      </div>

      <div class="success-info">
        <div class="success-item">
          <i class="fas fa-sign-in-alt"></i>
          <span>You can now sign in using this passkey</span>
        </div>
        <div class="success-item">
          <i class="fas fa-cog"></i>
          <span>Manage your passkeys in account settings</span>
        </div>
      </div>

      <div class="step-actions">
        <button class="btn-primary btn-full" @click="finishSetup">
          <i class="fas fa-check"></i>
          Done
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.passkey-enrollment {
  width: 100%;
  max-width: 480px;
}

.enrollment-step {
  background: var(--calid-card-bg, #111827);
  border-radius: 16px;
  padding: 2rem;
}

.step-header {
  text-align: center;
  margin-bottom: 2rem;
}

.step-icon {
  width: 72px;
  height: 72px;
  margin: 0 auto 1rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.step-icon i {
  font-size: 32px;
  color: white;
}

.step-icon-registering {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
}

.step-icon-success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  animation: success-bounce 0.5s ease;
}

@keyframes success-bounce {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

.step-header h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--calid-text-primary, #f0f0f5);
  margin-bottom: 0.5rem;
}

.step-header p {
  color: var(--calid-text-secondary, #94a3b8);
  font-size: 0.95rem;
  margin: 0;
}

.passkey-features {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
}

.feature {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.03));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.06));
  border-radius: 12px;
}

.feature-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.feature-icon i {
  color: white;
  font-size: 1rem;
}

.feature-text {
  display: flex;
  flex-direction: column;
}

.feature-text strong {
  color: var(--calid-text-primary, #f0f0f5);
  font-size: 0.95rem;
}

.feature-text span {
  color: var(--calid-text-secondary, #94a3b8);
  font-size: 0.85rem;
}

.name-input-container {
  margin-bottom: 1.5rem;
}

.name-input-container label {
  display: block;
  font-weight: 500;
  color: var(--calid-text-secondary, #94a3b8);
  margin-bottom: 0.5rem;
}

.name-input {
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid var(--calid-border, rgba(255, 255, 255, 0.1));
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.2s ease;
  outline: none;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.03));
  color: var(--calid-text-primary, #f0f0f5);
}

.name-input::placeholder {
  color: var(--calid-text-tertiary, #64748b);
}

.name-input:focus {
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
}

.name-input-container small {
  display: block;
  margin-top: 0.5rem;
  color: var(--calid-text-tertiary, #64748b);
  font-size: 0.85rem;
}

.error-message {
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #f87171;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.warning-message {
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.2);
  color: #fbbf24;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.registering-animation {
  position: relative;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pulse-ring {
  position: absolute;
  width: 80px;
  height: 80px;
  border: 2px solid #00d4ff;
  border-radius: 50%;
  animation: pulse-ring 2s infinite;
  opacity: 0;
}

.pulse-ring.delay-1 {
  animation-delay: 0.5s;
}

.pulse-ring.delay-2 {
  animation-delay: 1s;
}

@keyframes pulse-ring {
  0% {
    transform: scale(0.8);
    opacity: 1;
  }
  100% {
    transform: scale(2);
    opacity: 0;
  }
}

.fingerprint-animation i {
  animation: fingerprint-pulse 1.5s infinite;
}

@keyframes fingerprint-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.device-prompt {
  text-align: center;
  color: var(--calid-text-secondary, #94a3b8);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
}

.device-prompt i {
  animation: point 1s infinite;
}

@keyframes point {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}

.success-info {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
}

.success-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 10px;
  color: #34d399;
}

.success-item i {
  font-size: 1.25rem;
}

.step-actions {
  display: flex;
  gap: 1rem;
  justify-content: space-between;
}

.btn-primary,
.btn-secondary {
  padding: 0.875rem 1.5rem;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-primary {
  background: #10b981;
  color: white;
  flex: 1;
}

.btn-primary:hover:not(:disabled) {
  background: #059669;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.05));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.1));
  color: var(--calid-text-secondary, #94a3b8);
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.1);
}

.btn-full {
  width: 100%;
}

/* Light mode overrides */
:root:not(.dark-version) .enrollment-step {
  background: white;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

:root:not(.dark-version) .step-header h3 {
  color: #1f2937;
}

:root:not(.dark-version) .step-header p {
  color: #6b7280;
}

:root:not(.dark-version) .feature {
  background: #f9fafb;
  border-color: #e5e7eb;
}

:root:not(.dark-version) .feature-text strong {
  color: #1f2937;
}

:root:not(.dark-version) .feature-text span {
  color: #6b7280;
}

:root:not(.dark-version) .name-input-container label {
  color: #374151;
}

:root:not(.dark-version) .name-input {
  background: #f9fafb;
  border-color: #e5e7eb;
  color: #1f2937;
}

:root:not(.dark-version) .success-item {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #065f46;
}

:root:not(.dark-version) .btn-secondary {
  background: #f3f4f6;
  border-color: #e5e7eb;
  color: #374151;
}

:root:not(.dark-version) .btn-secondary:hover {
  background: #e5e7eb;
}
</style>
